import { AudioAsesoria, Evidencia } from '@/types/types';
import type { AsesoriaEstado } from '@/utils/asesoriaStatus';
import { api } from './api';

export interface CorreccionContextCelda {
  ntcId: number;
  orden: number;
  requisitoId: string | null;
  numeral: string | null;
  descripcion: string | null;
  calificacion: string | null;
  evaluacionRequisitoId?: number | null;
  campos?: CorreccionContextCampo | null;
}

export interface CorreccionContextCampo {
  rechazado: boolean;
  yaCorregido: boolean;
  comentarioAdmin: string | null;
}

export interface CorreccionContextTerna {
  evaluacionTernaId: number;
  ternaId: number | null;
  grupo: string | null;
  observaciones: string | null;
  planMejora: string | null;
  fechaImplementacion: string | null;
  evidenciaIds: number[];
  campos: {
    observaciones: CorreccionContextCampo;
    planMejora: CorreccionContextCampo;
    fechaImplementacion: CorreccionContextCampo;
  };
  celdas: CorreccionContextCelda[];
}

export interface CorreccionContextEvidencia {
  evidenciaId: number;
  nombre: string;
  comentarioAdmin: string | null;
  yaCorregido: boolean;
  reemplazoEvidenciaId: number | null;
}

export interface CorreccionContextAudio {
  audioId: number;
  fechaGrabacion: string;
  duracionSeg: number;
  comentarioAdmin: string | null;
  yaCorregido: boolean;
  reemplazoAudioId: number | null;
}

export interface CorreccionesContext {
  success: boolean;
  rondaActual: number;
  columnas: Array<{ ntcId: number; nombre: string; orden: number }>;
  ternasConCorreccion: CorreccionContextTerna[];
  evidenciasRechazadas: CorreccionContextEvidencia[];
  audiosRechazados: CorreccionContextAudio[];
}

export interface Reunion {
  reunionId: string;
  fechaInicio: string;
  fechaFin: string;
  motivo: string;
  correos: string;
  asesoriaId: number;
  estado: 'PROGRAMADA' | 'COMPLETADA' | 'CANCELADA';
}
export interface OtraAsesoria {
  asesoriaId: number;
  asesorId: number;
  asesorNombre: string;
  fechaInicio: string;
  fechaFin: string | null;
  estado: string;
  objetivo: string;
  orden: number | null;
}

export interface AsesoriaDetailResponse {
  asesoriaId: number;
  empresa: { nit: string; nombre: string; asesoriaActivaId?: number | null };
  asesor: { nombre: string; asesorId: number | null };
  estado: AsesoriaEstado;
  autoAprobar?: boolean;
  orden: number | null;
  /** Enlace del acta generada; null si la asesoría no tiene acta. */
  actaLink?: string | null;
  reuniones: Reunion[];
  objetivo: string;
  recomendaciones: string | null;
  compromisos: string | null;
  conclusiones: string | null;
  horas: number | null;
  fechaInicio: string;
  fechaFin: string | null;
  // Al devolver a correcciones, el admin decide si el asesor puede subir evidencias nuevas
  // (true, por defecto) o solo reemplazar las rechazadas (false).
  permiteSubirEvidenciasNuevas?: boolean;
  evidencias: Evidencia[];
  audios?: AudioAsesoria[];
  correoEmpresa?: string | null;
  otrasAsesorias?: OtraAsesoria[];
  resumenPorNtc?: Array<{
    ntcId: number;
    nombre: string;
    total: number;
    antes: { C: number; NC: number; CP: number; NA: number; pct: number };
    despues: { C: number; NC: number; CP: number; NA: number; pct: number };
  }>;
}

export interface AsesoriaInfoResponse {
  asesoriaId: number;
  estado: AsesoriaEstado;
  objetivo: string;
  recomendaciones: string | null;
  horas: number | null;
  fechaInicio: string;
  fechaFin: string | null;
}

export interface AsesoriaIniciarResponse {
  success: boolean;
  asesoria: {
    id: string;
    objetivo: string;
    estado: string;
    fechaInicio: string;
  };
  empresa: {
    nit: string;
    nombre: string;
    representante: string;
  };
}

export interface ReunionNoCompletada {
  id: number;
  motivo: string;
  estado: string;
  idAsesoria: number;
}

export interface FinalizarAsesoriaResponse {
  success: boolean;
  message: string;
}

export interface SimularFinalizarAsesoriaResponse {
  success: boolean;
  ok: boolean;
  simulacion: true;
  message: string;
  motivo?: string;
  formUrl?: string | null;
  asesoriaId?: number;
}

export interface AudioProcessingStatusResponse {
  success: true;
  nit: string;
  asesoriaActivaId: number;
  hasAudiosEnProcesamiento: boolean;
  totalJobsEnProcesamiento: number;
}

export const asesoriasService = {
  async getById(asesoriaId: number, nit: string): Promise<{ success: true; asesoria: AsesoriaDetailResponse }> {
    return api.get<{ success: true; asesoria: AsesoriaDetailResponse }>(`/empresas/${nit}/asesorias/${Number(asesoriaId)}`);
  },

  async getActiva(nit: string): Promise<{ success: true; asesoria: AsesoriaDetailResponse }> {
    return api.get<{ success: true; asesoria: AsesoriaDetailResponse }>(`/empresas/${nit}/asesorias?estado=activa`);
  },

  async setEnFoco(nit: string, asesoriaId: number): Promise<{ success: boolean }> {
    return api.patch(`/empresas/${nit}/asesorias/foco`, { asesoriaId });
  },

  async revertir(nit: string, asesoriaId: number): Promise<{ success: boolean; message: string }> {
    return api.patch(`/empresas/${nit}/asesoria-activa/revertir/${asesoriaId}`, {});
  },

  async iniciar(nit: string, objetivo: string): Promise<AsesoriaIniciarResponse> {
    return api.post<AsesoriaIniciarResponse>(`/empresas/${nit}/asesorias`, {
      objetivo,
    });
  },

  async finalizar(
    nit: string,
    asesoriaId: number,
    compromisos: string,
    conclusiones: string,
    direccion: string | null,
    fecha?: string
  ): Promise<FinalizarAsesoriaResponse> {
    const direccionLimpia = typeof direccion === 'string' ? direccion.trim() : direccion;

    return api.post<FinalizarAsesoriaResponse>(`/empresas/${nit}/asesoria-activa/estado`, {
      estado: 'EN_REVISION',
      asesoriaId,
      fecha: fecha ?? new Date().toISOString(),
      compromisos: compromisos.trim(),
      conclusiones: conclusiones.trim(),
      direccion: direccionLimpia,
    });
  },

  async simularFinalizar(
    nit: string,
    asesoriaId: number,
    compromisos: string,
    conclusiones: string,
    direccion: string | null,
    fecha?: string
  ): Promise<SimularFinalizarAsesoriaResponse> {
    const direccionLimpia = typeof direccion === 'string' ? direccion.trim() : direccion;

    return api.post<SimularFinalizarAsesoriaResponse>(`/empresas/${nit}/asesoria-activa/simular-estado`, {
      estado: 'EN_REVISION',
      asesoriaId,
      fecha: fecha ?? new Date().toISOString(),
      compromisos: compromisos.trim(),
      conclusiones: conclusiones.trim(),
      direccion: direccionLimpia,
    });
  },

  async getAudioProcessingStatus(nit: string): Promise<AudioProcessingStatusResponse> {
    return api.get<AudioProcessingStatusResponse>(`/empresas/${encodeURIComponent(nit)}/asesoria-activa/audio-processing-status`);
  },

  async actualizarActiva(
    nit: string,
    objetivo: string,
    fechaInicio: string
  ): Promise<{ success: boolean; asesoria: AsesoriaDetailResponse }> {
    return api.patch<{ success: boolean; asesoria: AsesoriaDetailResponse }>(`/empresas/${nit}/asesoria-activa`, {
      objetivo,
      fechaInicio,
    });
  },

  async cancelarVerificacion(nit: string): Promise<{ success: boolean; message: string }> {
    return api.post<{ success: boolean; message: string }>(`/empresas/${nit}/asesoria-activa/cancelar-verificacion`, {});
  },

  async responderRevision(
    nit: string,
    asesoriaId: number,
    payload: { elementoTipo: string; elementoId: string; respuesta: string }
  ): Promise<{ success: boolean }> {
    return api.post<{ success: boolean }>(`/empresas/${nit}/asesorias/${asesoriaId}/responder-revision`, payload);
  },

  async editarRespuesta(
    nit: string,
    asesoriaId: number,
    payload: { elementoTipo: string; elementoId: string; respuesta: string }
  ): Promise<{ success: boolean }> {
    return api.patch<{ success: boolean }>(`/empresas/${nit}/asesorias/${asesoriaId}/editar-respuesta`, payload);
  },

  async vincularAudioReemplazo(
    nit: string,
    asesoriaId: number,
    audioId: number,
    reemplazaAudioId: number | null
  ): Promise<{ success: boolean }> {
    return api.patch<{ success: boolean }>(`/empresas/${nit}/asesorias/${asesoriaId}/audios/${audioId}/reemplazo`, { reemplazaAudioId });
  },

  async marcarCorregido(
    nit: string,
    asesoriaId: number,
    elementos: Array<{ elementoTipo: string; elementoId: string }>
  ): Promise<{ success: boolean }> {
    return api.post<{ success: boolean }>(`/empresas/${nit}/asesorias/${asesoriaId}/marcar-corregido`, { elementos });
  },

  async getCorreccionesContext(nit: string, asesoriaId: number): Promise<CorreccionesContext> {
    return api.get<CorreccionesContext>(`/empresas/${nit}/asesorias/${asesoriaId}/correcciones-context`);
  },
};
